/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import type { Channel } from 'stream-chat';
import { format } from 'date-fns';

const LIMIT = 100;

export interface ISharedAttachment {
  image?: string;
  title: string;
  description?: string;
  link?: string;
  type: 'link' | 'document' | 'photo';
  createdAt: string;
  id: string;
  messageId: string;
}

interface IAttachments {
  photos: Array<ISharedAttachment>;
  links: Record<string, Array<ISharedAttachment>>;
  documents: Record<string, Array<ISharedAttachment>>;
}

/*
  Two Loading States are being used for better UX
  initial: when we have never loaded the attachments, we want to show a loading indicator for the entire content
  query: for when the user has loaded some content, and they scroll bottom, we want to keep the current content and show a new loading indicator at the bottom of the DOM
*/
export interface ILoadingAttachments {
  initial: boolean;
  query: boolean;
}

const useMediaAttachments = (channel: Channel) => {
  /*
  hasMoreAttachmentsOffsetId is using 3 states to handle fetching attachments
  undefined: initial state (implicit) <- used for initial call to getStream mimiking index offset 0
  "string": used to know what the next offset ID is going to be and whether or not we have more to fetch
  null: means we are all done fetching the records (no more exist)
  */
  const [hasMoreAttachmentsOffsetId, setHasMoreAttachmentsOffsetId] = useState<
    string | null
  >();
  const [loading, setLoading] = useState<ILoadingAttachments>({
    initial: true,
    query: false,
  });
  const [attachments, setAttachments] = useState<IAttachments>({
    photos: [],
    links: {},
    documents: {},
  });

  const formatMonthYear = (dateStr: string) => {
    const date = new Date(dateStr);
    return format(date, 'MMMM yyyy');
  };

  useEffect(() => {
    if (hasMoreAttachmentsOffsetId === null) {
      return;
    }

    let isCancelled = false;

    const fetchMoreAttachments = async () => {
      if (isCancelled) {
        return;
      }

      if (!loading.initial) {
        setLoading(prev => ({ ...prev, query: true }));
      }

      try {
        const res = await channel.query({
          messages: { limit: LIMIT, id_lt: hasMoreAttachmentsOffsetId },
        });

        const { messages } = res;

        if (messages.length < LIMIT) {
          setHasMoreAttachmentsOffsetId(null);
        } else {
          setHasMoreAttachmentsOffsetId(messages.at(0)?.id || null);
        }

        const newAttachments: IAttachments = {
          photos: [],
          links: {},
          documents: {},
        };

        messages.forEach(msg => {
          const monthYear = formatMonthYear(msg.created_at);

          msg.attachments?.forEach((att, idx) => {
            const baseItem: ISharedAttachment = {
              id: `${msg.id}-${idx}`,
              messageId: msg.id,
              createdAt: msg.created_at,
              title: att.title || att.fallback || '',
              description: att.text || att.author_name,
              image: att.image_url || att.thumb_url,
              link: att.title_link || att.og_scrape_url,
              type: 'photo',
            };

            if (
              (att.type === 'image' || att.type === 'video') &&
              !att.og_scrape_url
            ) {
              newAttachments.photos.push({
                ...baseItem,
                image: att.image_url || att.asset_url,
                link: att.thumb_url,
              });
            } else if (att.type === 'giphy') {
              newAttachments.photos.push({
                ...baseItem,
                image: att.image_url || att.thumb_url,
                link: att.thumb_url,
              });
            } else if (att.type === 'file') {
              const docItem: ISharedAttachment = {
                ...baseItem,
                type: 'document',
                title: att.title || att.fallback || '',
                description: `${att.mime_type || ''} • ${att.file_size || ''}`,
                link: att.asset_url,
              };
              if (!newAttachments.documents[monthYear]) {
                newAttachments.documents[monthYear] = [];
              }
              newAttachments.documents[monthYear].push(docItem);
            } else if (att.og_scrape_url) {
              const linkItem: ISharedAttachment = {
                ...baseItem,
                type: 'link',
                title: att.title || att.og_scrape_url,
                image: att.thumb_url || att.image_url,
                link: att.og_scrape_url,
              };
              if (!newAttachments.links[monthYear]) {
                newAttachments.links[monthYear] = [];
              }
              newAttachments.links[monthYear].push(linkItem);
            }
          });
        });

        setAttachments(prev => ({
          photos: [...prev.photos, ...newAttachments.photos],
          links: {
            ...prev.links,
            ...Object.fromEntries(
              Object.entries(newAttachments.links).map(([month, items]) => [
                month,
                [...(prev.links[month] || []), ...items],
              ]),
            ),
          },
          documents: {
            ...prev.documents,
            ...Object.fromEntries(
              Object.entries(newAttachments.documents).map(([month, items]) => [
                month,
                [...(prev.documents[month] || []), ...items],
              ]),
            ),
          },
        }));
      } catch (error) {
        setHasMoreAttachmentsOffsetId(null);
        // eslint-disable-next-line no-console
        console.error('Error fetching attachments:', error);
      }

      setLoading({ initial: false, query: false });
    };

    fetchMoreAttachments();

    return () => {
      isCancelled = true;
    };
  }, [channel, hasMoreAttachmentsOffsetId, loading.initial]);

  return {
    loading,
    ...attachments,
  };
};

export default useMediaAttachments;
